import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  useToast,
  useColorModeValue,
  IconButton,
  Image,
  Center,
  Badge
} from '@chakra-ui/react';
import { FiArrowLeft, FiPlay, FiEdit, FiTrash2, FiDownload } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';

interface EpisodeData {
  id: string;
  title: string;
  episodeNumber: string;
  thumbnail: string;
  duration: string;
  description: string;
  videoUrl: string;
  audioUrl?: string;
  trailerUrl?: string;
  subtitleUrl?: string;
  seriesId: string;
  seasonId: string;
}

const EpisodeDetail: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { episodeId } = useParams<{ episodeId: string }>();
  const toast = useToast();
  
  const [episode, setEpisode] = useState<EpisodeData | null>(null);
  const [loading, setLoading] = useState(true);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('gray.50', 'gray.700');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadEpisode = useCallback(async () => {
    try {
      setLoading(true);
      // Load episode from localStorage
      const seriesId = localStorage.getItem('currentSeriesId');
      if (seriesId) {
        const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
        if (savedEpisodes) {
          const episodesData = JSON.parse(savedEpisodes);
          const foundEpisode = episodesData.find((ep: EpisodeData) => ep.id === episodeId);
          if (foundEpisode) {
            setEpisode(foundEpisode);
          } else {
            toast({
              title: 'Episode not found',
              description: 'The requested episode could not be found.',
              status: 'error',
              duration: 3000,
              isClosable: true,
            });
            navigate(-1);
          }
        }
      }
    } catch (error) {
      console.error('Error loading episode:', error);
      toast({
        title: 'Error',
        description: 'Failed to load episode details.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [episodeId, navigate, toast]);

  useEffect(() => {
    if (episodeId) {
      loadEpisode();
    }
  }, [episodeId, loadEpisode]);

  const handlePlay = () => {
    if (episode?.videoUrl) {
      // Navigate to video player with episode data
      navigate(`/player/${episode.id}`, {
        state: {
          src: episode.videoUrl,
          title: episode.title,
          subtitles: episode.subtitleUrl ? [episode.subtitleUrl] : [],
          creatorId: user?.uid || '',
          contentId: episode.id,
          uploaderName: 'Creator',
          creatorAvatar: ''
        }
      });
    }
  };

  const handleEdit = () => {
    // Navigate to edit episode page
    navigate(`/episode/${episodeId}/edit`);
  };

  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this episode? This action cannot be undone.')) {
      // Delete episode from localStorage
      const seriesId = episode?.seriesId;
      if (seriesId) {
        const savedEpisodes = localStorage.getItem(`episodes-${seriesId}`);
        if (savedEpisodes) {
          const episodesData = JSON.parse(savedEpisodes);
          const updatedEpisodes = episodesData.filter((ep: EpisodeData) => ep.id !== episodeId);
          localStorage.setItem(`episodes-${seriesId}`, JSON.stringify(updatedEpisodes));
          
          // Update season episode count
          const seasonsKey = `seasons-${seriesId}`;
          const existingSeasons = JSON.parse(localStorage.getItem(seasonsKey) || '[]');
          const updatedSeasons = existingSeasons.map((season: any) => {
            if (season.id === episode?.seasonId) {
              return { ...season, episodeCount: Math.max(0, season.episodeCount - 1) };
            }
            return season;
          });
          localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
          
          // Refresh seasons if function is available
          if ((window as any).refreshSeasons) {
            (window as any).refreshSeasons();
          }
        }
      }
      
      toast({
        title: 'Episode deleted',
        description: 'The episode has been successfully deleted.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      // Navigate back to episodes management
      navigate(`/series/${episode?.seriesId}/season/${episode?.seasonId}/episodes`);
    }
  };

  const handleDownload = () => {
    if (episode?.videoUrl) {
      // Create a temporary link to download the video
      const link = document.createElement('a');
      link.href = episode.videoUrl;
      link.download = `${episode.title}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to view episodes.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  if (loading) {
    return (
      <Center h="50vh">
        <Text>Loading episode...</Text>
      </Center>
    );
  }

  if (!episode) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Episode not found.
          </Text>
          <Button onClick={() => navigate(-1)} colorScheme="purple">
            Go Back
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Episode Details
          </Text>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="4xl" mx="auto">
        <VStack spacing={6} align="stretch">
          {/* Episode Info Card */}
          <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor="gray.200">
            <HStack gap={6} align="start">
              {/* Episode Thumbnail */}
              <Box position="relative" flex="0 0 200px">
                <Image
                  src={episode.thumbnail || '/placeholder-thumbnail.jpg'}
                  alt={episode.title}
                  w="200px"
                  h="120px"
                  objectFit="cover"
                  borderRadius="md"
                  fallback={
                    <Box
                      w="200px"
                      h="120px"
                      bg="gray.200"
                      borderRadius="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <IconWrapper icon={FiPlay} size={32} color="gray.400" />
                    </Box>
                  }
                />
                {/* Play Button Overlay */}
                <Box
                  position="absolute"
                  top="50%"
                  left="50%"
                  transform="translate(-50%, -50%)"
                  bg="rgba(0,0,0,0.7)"
                  borderRadius="full"
                  p={3}
                  cursor="pointer"
                  onClick={handlePlay}
                  _hover={{ bg: 'rgba(0,0,0,0.8)' }}
                >
                  <IconWrapper icon={FiPlay} size={24} color="white" />
                </Box>
              </Box>

              {/* Episode Details */}
              <VStack align="start" flex="1" gap={3}>
                <HStack w="full" justify="space-between">
                  <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                    {episode.title}
                  </Text>
                  <Badge colorScheme="purple" variant="subtle">
                    Episode {episode.episodeNumber}
                  </Badge>
                </HStack>
                
                <Text fontSize="lg" color={labelColor}>
                  Duration: {episode.duration}
                </Text>
                
                <Text color={textColor}>
                  {episode.description}
                </Text>

                {/* Action Buttons */}
                <HStack gap={3} mt={4}>
                  <Button
                    colorScheme="purple"
                    leftIcon={<IconWrapper icon={FiPlay} size={16} />}
                    onClick={handlePlay}
                  >
                    Play Episode
                  </Button>
                  <Button
                    variant="outline"
                    leftIcon={<IconWrapper icon={FiEdit} size={16} />}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    colorScheme="red"
                    leftIcon={<IconWrapper icon={FiTrash2} size={16} />}
                    onClick={handleDelete}
                  >
                    Delete
                  </Button>
                  <Button
                    variant="outline"
                    leftIcon={<IconWrapper icon={FiDownload} size={16} />}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                </HStack>
              </VStack>
            </HStack>
          </Box>

          {/* Additional Files */}
          {(episode.audioUrl || episode.trailerUrl || episode.subtitleUrl) && (
            <Box bg={cardBg} borderRadius="lg" p={6} border="1px solid" borderColor="gray.200">
              <Text fontSize="lg" fontWeight="bold" color={textColor} mb={4}>
                Additional Files
              </Text>
              <VStack align="stretch" gap={2}>
                {episode.audioUrl && (
                  <HStack justify="space-between">
                    <Text color={textColor}>Audio Track</Text>
                    <Button size="sm" variant="outline" onClick={() => window.open(episode.audioUrl, '_blank')}>
                      Download
                    </Button>
                  </HStack>
                )}
                {episode.trailerUrl && (
                  <HStack justify="space-between">
                    <Text color={textColor}>Trailer</Text>
                    <Button size="sm" variant="outline" onClick={() => window.open(episode.trailerUrl, '_blank')}>
                      Download
                    </Button>
                  </HStack>
                )}
                {episode.subtitleUrl && (
                  <HStack justify="space-between">
                    <Text color={textColor}>Subtitle</Text>
                    <Button size="sm" variant="outline" onClick={() => window.open(episode.subtitleUrl, '_blank')}>
                      Download
                    </Button>
                  </HStack>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default EpisodeDetail;
